import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

// UI Components
import {
  DrawerContainer,
  DrawerContent,
  CampaignsNavigation,
  Banner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerImage,
  gradients,
} from "../../../components/ui";

// Character image for banner
import campanasCharacter from "../../../assets/characters-banners/campañas.png";

// New Design System Components
import {
  CampaignsContainer,
  PageHeader,
  PageTitle,
  MetricsTop,
  MetricMini,
  MetricIcon,
  MetricContent,
  MetricLabel,
  MetricValue,
  MetricUnavailable,
  MetricRetryButton,
} from "./Campaigns.style";

// Legacy Layout Components (for step views that haven't been migrated yet)
import {
  RowFullHeight,
  Row,
} from "./BroadcastView.styles";

// Step Components
import BroadcastTable from "./BroadcastTable.component";
import BroadcastSelectTagView from "./BroadcastSelectTagView";
import BroadcastSelectFileView from "./broadcast-select-file/views/broadcast-select-file-view";
import BroadcastType from "./BroadcastType.component";
import BroadcastSelectTemplate from "./BroadcastSelectTemplate";
import BroadcastName from "./BroadcastName";
import BroadcastSelectDate from "./BroadcastSelectDate";
import BroadcastPreview from "./BroadcastPreview";
import BroadcastPayment from "./BroadcastPayment";
import BroadcastSelectMultimedia from "./BroadcastSelectMultimedia";
import BroadcastCreateTag from "./BroadcastCreateTag";
import LoaderComponent from "../../../components/loader/Loader.component";
import {
  MESSAGING_LIMIT_KIND,
  resolveMessagingLimit,
  formatMessagingLimit,
} from "./helpers/messaging-limit";
import {
  useWaAccountStatus,
  WA_ACCOUNT_STATUS_PHASE,
} from "./hooks/use-wa-account-status";

const qualityRating = {
  High: "Alta",
  Medium: "Media",
  Low: "Baja",
  NA: "NA",
};

const qualityColor = {
  High: "#166534",
  Medium: "#92400E",
  Low: "#991B1B",
};

const BroadcastView = (props) => {
  const history = useHistory();
  const waChannels = useSelector(
    (state) => state.channels.data.whatsappChannels
  );
  // The list is final once the channels duck reports `success`, or `error`
  // for a load that failed. Until then a channel missing from it may still
  // arrive, so it is not an error yet.
  const channelsLoaded = useSelector((state) =>
    Boolean(state.channels.success || state.channels.error)
  );
  const channelId = useSelector((state) => state.campaign.create.channelId);

  const [activeView, setActiveView] = useState("create");
  const retryButtonRef = useRef(null);
  const isUserRetryPending = useRef(false);

  // Follows the campaign's channel in redux. The channel selector of the table
  // writes it there, so the container needs no callback from the table.
  const { phase, accountStatus, channel, canRetry, retry } = useWaAccountStatus({
    channelId,
    channels: waChannels,
    channelsLoaded,
  });
  const isLoading = phase === WA_ACCOUNT_STATUS_PHASE.LOADING;
  const isReady = phase === WA_ACCOUNT_STATUS_PHASE.READY;

  const currentStep = props?.computedMatch?.params?.currentStep;

  // Resolved once here: the card, the over-limit warning and the request body
  // must all read the same limit. Only a status that was read counts: while it
  // is loading, and after an error, the limit is unknown.
  const rawTier = isReady ? accountStatus.current_limit : undefined;
  const messagingLimit = resolveMessagingLimit(rawTier);

  const qualityKey = isReady ? accountStatus.current_quality_rating : undefined;
  const hasKnownQuality = Object.prototype.hasOwnProperty.call(
    qualityColor,
    qualityKey
  );

  useEffect(() => {
    if (!channelId) history.push("/broadcast/table");
  }, [channelId]);

  // The retry button leaves the screen while its request is loading, and the
  // keyboard focus is dropped with it. It is given back when that same retry
  // ends with the button on screen again, and at no other time.
  const handleRetry = () => {
    isUserRetryPending.current = true;
    retry();
  };

  // A channel change replaces the request the user retried: whatever ends
  // after it is not the outcome of that retry.
  useEffect(() => {
    isUserRetryPending.current = false;
  }, [channelId]);

  useEffect(() => {
    if (isLoading || !isUserRetryPending.current) return;
    isUserRetryPending.current = false;
    // Only when the focus is still dropped: never take it from wherever the
    // user moved it while waiting.
    const isFocusDropped =
      !document.activeElement || document.activeElement === document.body;
    if (isFocusDropped && retryButtonRef.current) retryButtonRef.current.focus();
  }, [isLoading]);

  const handleViewChange = (viewKey) => {
    setActiveView(viewKey);
    // Navigate based on view selection
    if (viewKey === "create") {
      history.push("/broadcast/table");
    }
    // Other views (scheduled, audiences, reports) are disabled for now
  };

  // The "mensajes" suffix only makes sense next to a number
  const renderDailyLimit = () => {
    if (messagingLimit.kind === MESSAGING_LIMIT_KIND.LIMITED) {
      return (
        <>
          {formatMessagingLimit(messagingLimit)}{" "}
          <span>mensajes</span>
        </>
      );
    }
    const limitText = formatMessagingLimit(messagingLimit);
    if (limitText) return limitText;
    // Unknown for any reason: failed call, unknown tier or channel not found.
    // The retry is only offered when there is a channel to ask for again.
    return (
      <MetricUnavailable>
        No disponible
        {canRetry && (
          <MetricRetryButton
            type="button"
            onClick={handleRetry}
            ref={retryButtonRef}
          >
            Reintentar
          </MetricRetryButton>
        )}
      </MetricUnavailable>
    );
  };

  // Render Metrics Section
  const renderMetrics = () => (
    <MetricsTop>
      <MetricMini>
        <MetricIcon bg="#EEF2FF" color="#4F46E5">
          <FontAwesomeIcon icon={faStar} />
        </MetricIcon>
        <MetricContent>
          <MetricLabel>Calidad de tu cuenta</MetricLabel>
          {/* A rating outside the known ones is unavailable, never a good one */}
          <MetricValue color={hasKnownQuality ? qualityColor[qualityKey] : undefined}>
            {isLoading ? (
              <LoaderComponent width="22" height="22" />
            ) : (
              hasKnownQuality ? qualityRating[qualityKey] : "No disponible"
            )}
          </MetricValue>
        </MetricContent>
      </MetricMini>
      <MetricMini>
        <MetricIcon bg="#F0FDF4" color="#16A34A">
          <FontAwesomeIcon icon={faInfoCircle} />
        </MetricIcon>
        <MetricContent>
          <MetricLabel>Límite de envío diario</MetricLabel>
          {/* Announced politely when the limit arrives or cannot be read */}
          <MetricValue role="status">
            {isLoading ? (
              <LoaderComponent width="22" height="22" />
            ) : (
              renderDailyLimit()
            )}
          </MetricValue>
        </MetricContent>
      </MetricMini>
    </MetricsTop>
  );

  // Render the main content based on current step
  const renderContent = () => {
    // For table view - render with new design
    if (currentStep === "table") {
      return (
        <CampaignsContainer>
          {/* Banner Section */}
          <Banner gradient={gradients.orange}>
            <BannerContent>
              <BannerTitle>Campañas de WhatsApp</BannerTitle>
              <BannerDescription>
                Envía mensajes masivos a tus contactos con plantillas aprobadas. Programa campañas y analiza resultados.
              </BannerDescription>
            </BannerContent>
            <BannerImage src={campanasCharacter} alt="Campañas" />
          </Banner>

          <PageHeader>
            <PageTitle>Mis Campañas</PageTitle>
            {renderMetrics()}
          </PageHeader>
          <RowFullHeight>
            <BroadcastTable
              qualityRating={qualityRating[channel?.qualityRating]}
            />
          </RowFullHeight>
        </CampaignsContainer>
      );
    }

    // For type selection - render with new design (no metrics)
    if (currentStep === "type") {
      return (
        <CampaignsContainer>
          <RowFullHeight>
            <BroadcastType />
          </RowFullHeight>
        </CampaignsContainer>
      );
    }

    // For other steps - use legacy layout for now
    return (
      <CampaignsContainer style={{ height: '100%' }}>
        {currentStep === "file" && (
          <RowFullHeight>
            <BroadcastSelectFileView />
          </RowFullHeight>
        )}

        {currentStep === "tags" && (
          <RowFullHeight>
            <BroadcastSelectTagView messagingLimit={messagingLimit} />
          </RowFullHeight>
        )}

        {currentStep === "selectTemplate" && (
          <RowFullHeight>
            <BroadcastSelectTemplate />
          </RowFullHeight>
        )}

        {currentStep === "selectDate" && (
          <RowFullHeight>
            <BroadcastSelectDate />
          </RowFullHeight>
        )}

        {currentStep === "preview" && (
          <RowFullHeight>
            <BroadcastPreview />
          </RowFullHeight>
        )}

        {currentStep === "pricing" && (
          <RowFullHeight>
            <BroadcastPayment
              messagingLimit={messagingLimit}
              accountStatusPhase={phase}
              rawTier={rawTier}
            />
          </RowFullHeight>
        )}

        {currentStep === "createTag" && (
          <RowFullHeight>
            <BroadcastCreateTag />
          </RowFullHeight>
        )}

        {currentStep === "name" && (
          <RowFullHeight>
            <BroadcastName />
          </RowFullHeight>
        )}

        {currentStep === "selectMultimedia" && (
          <Row>
            <BroadcastSelectMultimedia />
          </Row>
        )}
      </CampaignsContainer>
    );
  };

  return (
    <DrawerContainer>
      <CampaignsNavigation activeView={activeView} onViewChange={handleViewChange} />
      <DrawerContent>
        {renderContent()}
      </DrawerContent>
    </DrawerContainer>
  );
};

export default BroadcastView;
